import { HttpClient } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { environment } from '@environments/_current/environment';
import {
  AaptivRoles,
  User,
} from '@skyfit/core-data-library';
import {
  BehaviorSubject,
  catchError,
  Observable,
  of,
  Subscription,
  tap,
} from 'rxjs';
import { AdminTenantService } from '../admin-tenant/admin-tenant.service';
import { Logger } from '../logger/logger.service';

export enum UserPermission {
  READ_ACCOUNT_CREATE_ADMIN = 'read.createAdmin',
  READ_ACCOUNT_ADMIN_OPTION = 'read.accountAdminOption',
  READ_HOME_ACCOUNT_SETUP = 'read.homeAccountSetup',
  READ_USER_OPTIONS = 'read.userOptions',
  READ_USER_INVITE = 'read.userInvite',
}

@Injectable({
  providedIn: 'root',
})
export class AdminUserService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly adminTenantService = inject(AdminTenantService);
  private readonly userSource = new BehaviorSubject<User>(null);
  private readonly subscription = new Subscription();
  private readonly logger = inject(Logger);
  user$ = this.userSource.asObservable();

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  setUser(user: User) {
    this.userSource.next(user);
  }

  getUser(): User {
    return this.userSource.value;
  }

  isCurrentUserSuperAdmin(): boolean {
    return (this.userSource.value?.roles as string[])?.includes('ADMIN');
  }

  loadCurrentUser() {
    const url = `${environment.aaptivServiceUrl}/api/v1/user`;

    this.http.get(url).subscribe({
      next: (user: User) => {
        this.setUser(user);
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  getUserFromBackend(): Observable<any> {
    const url = `${environment.aaptivServiceUrl}/api/v1/user`;
    return this.http.get(url).pipe(
      tap((user) => {
        this.setUser(user);
      }),
      catchError((error) => {
        console.error(error);
        return of(undefined);
      })
    );
  }

  addAdminRole(
    tenantId: string,
    authId: string,
    userId: string
  ): Observable<any> {
    const url = `${environment.aaptivServiceUrl}/api/v1/user/${tenantId}/${authId}/updateToAdmin`;
    return this.http.patch(url, { id: userId });
  }

  addUserToTenant(
    userId: string,
    tenantId: string,
    cancelStripeService = true,
    resetRoles = true
  ) {
    const url = `${environment.aaptivServiceUrl}/api/v2/enterpriseUserGroup/${tenantId}/add`;
    return this.http.post(url, {
      id: userId,
      cancelStripeService,
      resetRoles,
    });
  }

  removeAdminUserRole(userId: string) {
    const url = `${environment.aaptivServiceUrl}/api/v2/admin/users/${userId}/removeAdminRole`;
    return this.http.patch(url, { id: userId });
  }

  hardDeleteUser(userId: string) {
    const url = `${environment.aaptivServiceUrl}/api/v2/admin/users/${userId}/destroy`;
    return this.http.delete(url);
  }

  hasPermission(permission: UserPermission) {
    const user = this.getUser();
    if (!user) {
      return false;
    }

    const tenant = this.adminTenantService.getTenant();
    const provisioningType = this.adminTenantService.getProvisioningType();
    if (tenant && !provisioningType) {
      this.logger.warn('Tenant has no provisioning type');
    }

    const tenantIsOAuth = this.adminTenantService.isSsoOAuthTenant();

    if ((user.roles as string[]).includes(AaptivRoles.superadmin)) {
      return true;
    } else if (!tenantIsOAuth) {
      return true;
    } else if (
      permission === UserPermission.READ_ACCOUNT_CREATE_ADMIN ||
      permission === UserPermission.READ_ACCOUNT_ADMIN_OPTION ||
      permission === UserPermission.READ_HOME_ACCOUNT_SETUP ||
      permission === UserPermission.READ_USER_OPTIONS ||
      permission === UserPermission.READ_USER_INVITE
    ) {
      return false;
    }
  }
}
